import React, { useState, useMemo, useEffect } from 'react'
import { useBetTracker, type TrackedBetSlip } from '../context/BetContext'
import { TrackedBetCard, checkBetResult } from './TrackedBetCard'
import { formatCurrency } from '../utils/odds'
import { useFeatureTracking, FeatureNames } from '../hooks/useActivityTracking'
import type { FixtureLite } from '../types'

type FilterTab = 'all' | 'pending' | 'settled'

// Export bets to CSV
function exportBetsToCSV(bets: TrackedBetSlip[]) {
  const headers = ['ID', 'Placed At', 'Selections', 'Odds', 'Stake', 'Status', 'Settled At', 'P/L']

  const rows = bets.map((bet) => {
    const selections = bet.selections.map((s) => `${s.fixture}: ${s.title}`).join('; ')
    const pl = bet.status === 'won' && bet.stake && bet.oddsDecimal
      ? (bet.stake * bet.oddsDecimal - bet.stake).toFixed(2)
      : bet.status === 'lost' && bet.stake
      ? (-bet.stake).toFixed(2)
      : '0.00'

    return [
      bet.id,
      new Date(bet.placedAt).toLocaleString(),
      `"${selections}"`,
      bet.oddsDecimal?.toFixed(2) || '',
      bet.stake?.toFixed(2) || '',
      bet.status,
      bet.settledAt ? new Date(bet.settledAt).toLocaleString() : '',
      pl,
    ].join(',')
  })

  const csv = [headers.join(','), ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = `statscout-bets-${new Date().toISOString().split('T')[0]}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

interface MyBetsPageProps {
  fixtures: FixtureLite[]
}

export function MyBetsPage({ fixtures }: MyBetsPageProps) {
  // Track feature usage
  useFeatureTracking(FeatureNames.MY_BETS)

  // Build a map of fixture ID to fixture for quick lookup
  const fixtureMap = useMemo(() => {
    const map = new Map<number, FixtureLite>()
    fixtures.forEach((f) => map.set(f.id, f))
    return map
  }, [fixtures])
  const {
    trackedBets,
    settleBet,
    removeBet,
    getTotalStaked,
    getPotentialReturns,
    getProfitLoss,
    getPendingCount,
  } = useBetTracker()

  // Auto-settle bets when all fixtures are complete (FT)
  useEffect(() => {
    if (fixtures.length === 0) return

    trackedBets
      .filter((bet) => bet.status === 'pending')
      .forEach((bet) => {
        const result = checkBetResult(bet, fixtureMap)
        if (result === 'won' || result === 'lost') {
          settleBet(bet.id, result)
        }
      })
  }, [fixtures, trackedBets, fixtureMap, settleBet])

  const [activeFilter, setActiveFilter] = useState<FilterTab>('all')

  const filteredBets = useMemo(() => {
    switch (activeFilter) {
      case 'pending':
        return trackedBets.filter((b) => b.status === 'pending')
      case 'settled':
        return trackedBets.filter((b) => b.status !== 'pending')
      default:
        return trackedBets
    }
  }, [trackedBets, activeFilter])

  const totalStaked = getTotalStaked()
  const potentialReturns = getPotentialReturns()
  const profitLoss = getProfitLoss()
  const pendingCount = getPendingCount()
  const settledCount = trackedBets.length - pendingCount

  const handleExport = () => {
    if (trackedBets.length === 0) return
    exportBetsToCSV(trackedBets)
  }

  return (
    <div className="my-bets-page">
      <div className="my-bets-header">
        <h1 className="my-bets-title">My Bets</h1>
        {trackedBets.length > 0 && (
          <button className="export-btn" onClick={handleExport} title="Export to CSV">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export
          </button>
        )}
      </div>

      <div className="bet-summary">
        <div className="summary-card">
          <div className="summary-label">Total Staked</div>
          <div className="summary-value">{formatCurrency(totalStaked)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Pending Returns</div>
          <div className="summary-value">{formatCurrency(potentialReturns)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Profit / Loss</div>
          <div className={`summary-value ${profitLoss >= 0 ? 'positive' : 'negative'}`}>
            {profitLoss >= 0 ? '+' : ''}{formatCurrency(profitLoss)}
          </div>
        </div>
      </div>

      <div className="filter-tabs">
        <button
          className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          All ({trackedBets.length})
        </button>
        <button
          className={`filter-tab ${activeFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveFilter('pending')}
        >
          Pending ({pendingCount})
        </button>
        <button
          className={`filter-tab ${activeFilter === 'settled' ? 'active' : ''}`}
          onClick={() => setActiveFilter('settled')}
        >
          Settled ({settledCount})
        </button>
      </div>

      <div className="tracked-bets-list">
        {filteredBets.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <div className="empty-text">
              {activeFilter === 'all'
                ? 'No bets tracked yet.\nAdd bets from your bet builder to start tracking.'
                : activeFilter === 'pending'
                ? 'No pending bets.'
                : 'No settled bets yet.'}
            </div>
          </div>
        ) : (
          filteredBets.map((bet) => (
            <TrackedBetCard
              key={bet.id}
              bet={bet}
              fixtureMap={fixtureMap}
              onSettle={settleBet}
              onRemove={removeBet}
            />
          ))
        )}
      </div>
    </div>
  )
}
